import { graphql, useLazyLoadQuery } from "react-relay";
import { OrgAdminScreenProps } from "./OrgAdmin";

import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import React from "react";
import { Box, AHText, useSpaceSize, Card, AHIcon, TouchableBox } from "arkh";

import { OrgAdminStructureQuery } from "./__generated__/OrgAdminStructureQuery.graphql";
import { ScrollView, View } from "react-native";
import { MoreButton } from "../../../common/MoreButton";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import * as styles from "./OrgAdminStructure.module.css";
import { useNavigation } from "@react-navigation/native";
import { UserLoggedInNavigatorParamList } from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
type A<B> = B & {
  children?: B[];
};
function listToTree<
  T extends { readonly parentId: string | null; readonly id: string },
>(_list: readonly T[]): readonly A<T>[] {
  const map: Record<string, number> = {};
  let node: T;

  const list = _list.map((v, i) => ({ ...v, children: [] as T[] }));
  const roots = [];
  for (let i = 0; i < list.length; i += 1) {
    map[list[i].id] = i;
    list[i].children = [];
  }

  for (let i = 0; i < list.length; i += 1) {
    node = list[i];
    if (node.parentId) {
      list[map[node.parentId]]?.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

export function OrgAdminStructureScreen(
  props: OrgAdminScreenProps<"OrgAdminStructure">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey: string) => (
        <InnerOrgAdminStructureScreen {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerOrgAdminStructureScreen(
  props: OrgAdminScreenProps<"OrgAdminStructure"> & { fetchKey: string }
) {
  const data = useLazyLoadQuery<OrgAdminStructureQuery>(
    graphql`
      query OrgAdminStructureQuery {
        organisation @required(action: THROW) {
          id
          name
          workspaces {
            id
            name
            parentId
            timezone
            ownerStores {
              id
              name
            }
          }
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const tree = listToTree(data.organisation.workspaces);

  const createStore = (workspaceId: string, timezone: string) => {
    props.navigation.navigateDeprecated("OrgStoreCreate", {
      screen: "OrgStoreCreateScreen",
      params: {
        workspaceId,
        timezone,
      },
    });
  };
  const createWorkspace = (workspaceId: string, timezone: string) => {
    props.navigation.navigateDeprecated("OrgWorkspaceCreate", {
      screen: "OrgWorkspaceCreateScreen",
      params: {
        workspaceId,
        timezone,
      },
    });
  };

  const adminWorkspace = (workspaceId: string) => {
    props.navigation.navigateDeprecated("OrgWorkspaceAdmin", {
      workspaceId,
    });
  };
  return (
    <ScrollView>
      <Box spaceTop="medium" spaceBottom="xlarge" spaceHorizontal="medium">
        <Card defaultBorder>
          {tree.map((w) => (
            <ListItem
              key={w.id}
              data={w}
              createStore={createStore}
              createWorkspace={createWorkspace}
              adminWorkspace={adminWorkspace}
              isRoot
            />
          ))}
          <Box spaceBottom="medium" />
        </Card>
      </Box>
    </ScrollView>
  );
}

type L = {
  id: string;
  name: string;
  timezone: string;
  ownerStores: readonly { name: string; id: string }[];
  children?: readonly L[] | undefined;
};
function ListItem(props: {
  data: L;
  createWorkspace: (workspaceId: string, timezone: string) => void;
  createStore: (workspaceId: string, timezone: string) => void;
  adminWorkspace: (workspaceId: string) => void;
  isRoot?: boolean;
}) {
  const intl_tokens = useIntlTokens();
  const iconSize = useSpaceSize("medium");
  return (
    <View style={{ flexDirection: "row" }}>
      <Box>
        <Box>
          <Box
            spaceTop="medium"
            style={{ flex: 1, flexDirection: "row", alignItems: "center" }}
          >
            <Box
              style={{
                flexDirection: "row",
                alignItems: "center",
              }}
              spaceVertical="xsmall"
            >
              <Box
                style={{
                  width: iconSize,
                  alignItems: "center",
                }}
              >
                <AHIcon type="workspace" size="xsmall" />
              </Box>
              <AHText
                type="subtitle"
                spaceHorizontal="small"
                color="darkerText"
                spaceRight="small"
              >
                {props.data.name}
              </AHText>
            </Box>
            <MoreButton
              items={[
                {
                  label: intl_tokens.CreateWorkspace,
                  onPress: () => {
                    props.createWorkspace(props.data.id, props.data.timezone);
                  },
                  icon: "workspace",
                },
                {
                  label: intl_tokens.CreateUnit,
                  onPress: () => {
                    props.createStore(props.data.id, props.data.timezone);
                  },
                  icon: "store",
                },
                ...(props.isRoot
                  ? []
                  : [
                      {
                        label: intl_tokens.WorkspaceAdmin,
                        onPress: () => {
                          props.adminWorkspace(props.data.id);
                        },
                        icon: "workspace" as const,
                      },
                    ]),
              ]}
            >
              {(open) => (
                <TouchableBox
                  hitSlop={{ top: 20, bottom: 20, left: 20, right: 90 }}
                  onPress={open}
                >
                  <AHIcon type="more" size="medium" color="darkText" />
                </TouchableBox>
              )}
            </MoreButton>
          </Box>
        </Box>

        <Box spaceLeft="small">
          <Box
            spaceLeft="small"
            style={{ borderLeftWidth: 1 }}
            borderColor="border-strong"
          >
            <Box spaceLeft="small">
              <Box>
                {props.data.ownerStores?.map((s, i) => (
                  <Store key={i + ""} id={s.id} name={s.name} />
                ))}
              </Box>
              <Box>
                {props.data.children?.map((w) => (
                  <ListItem
                    key={w.id}
                    data={w}
                    createStore={props.createStore}
                    createWorkspace={props.createWorkspace}
                    adminWorkspace={props.adminWorkspace}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </View>
  );
}

function Store(props: { name: string; id: string }) {
  const iconSize = useSpaceSize("medium");
  const nav: NativeStackNavigationProp<UserLoggedInNavigatorParamList> =
    useNavigation();
  return (
    <View
      style={
        {
          $$css: true,
          _: styles.StoreContainer,
        } as any
      }
    >
      <Box
        spaceVertical="xsmall"
        style={{
          flexDirection: "row",
          overflow: "hidden",
        }}
      >
        <Box
          style={{
            width: iconSize,
            alignItems: "center",
          }}
        >
          <AHIcon size="xsmall" type="store" color="darkText" />
        </Box>
        <AHText spaceLeft="small" type="body" spaceRight="small">
          {props.name}
        </AHText>

        <View
          style={
            {
              $$css: true,
              _: styles.MoreContainer,
            } as any
          }
        >
          <TouchableBox
            hitSlop={{ top: 20, bottom: 20, left: 20, right: 90 }}
            onPress={() => {
              nav.navigateDeprecated("OrgStoreAdmin", {
                storeId: props.id,
              });
            }}
          >
            <AHIcon type="more" size="medium" color="darkText" />
          </TouchableBox>
        </View>
      </Box>
    </View>
  );
}
